"use client";

import React, { useState } from 'react';
import { Package } from '@/domain/entities/package.entity';
import { Theme } from '@/domain/entities/theme.entity';
import { Addon } from '@/domain/entities/addon.entity';
import { BookingSlot, SelectedAddon, CustomerInfo } from '@/domain/entities/booking.entity';
import { BookingFlowConfig } from '../booking-flow-config';
import { formatMYR } from '@/shared/utils/currency';
import { Clipboard as ClipboardIcon, AlertCircle, Phone, Download, CheckCircle2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { getImageUrl } from '@/shared/utils/image';
import Image from 'next/image';

interface ReviewStepProps {
    module: BookingFlowConfig["module"];
    texts: BookingFlowConfig["texts"];
    selectedPackage: Package | null;
    selectedThemes: Theme[];
    slots: BookingSlot[];
    selectedAddons: SelectedAddon[];
    customerInfo: CustomerInfo;
    addonsList: Addon[];
    packagePrice: number;
    themePrice: number;
    totalPrice: number;
    isConfirmed: boolean;
    onConfirmedChange: (confirmed: boolean) => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
    module,
    texts,
    selectedPackage,
    selectedThemes,
    slots,
    selectedAddons,
    customerInfo,
    addonsList,
    packagePrice,
    themePrice,
    totalPrice,
    isConfirmed,
    onConfirmedChange,
}) => {
    const [isCopied, setIsCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText("554062328498");
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleDownloadQr = () => {
        const link = document.createElement('a');
        link.href = '/qr-bank.jpeg';
        link.download = 'Leqiu-Creative-QR.jpeg';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleConfirmChange = (checked: boolean) => {
        onConfirmedChange(checked);
    };

    return (
        <div className="space-y-12 max-w-6xl mx-auto">
            <div className="text-center space-y-4">
                <h2 className="text-4xl lg:text-5xl font-serif text-foreground leading-tight">
                    {texts.finalReviewTitle}
                </h2>
                <p className="text-muted-foreground max-w-md mx-auto text-sm leading-relaxed">
                    {texts.finalReviewDescription}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Left: Summary */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="space-y-8"
                >
                    <div className="bg-card rounded-[2.5rem] p-10 border border-border/50 shadow-[0_15px_60px_-15px_rgba(0,0,0,0.03)]">
                        <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground/60 mb-8 ml-1">Session Invitation</h3>

                        <div className="space-y-8">
                            {/* Package */}
                            <div className="flex justify-between items-start">
                                <div className="space-y-2">
                                    <p className="text-2xl font-serif text-foreground leading-none">{selectedPackage?.name}</p>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-xs text-muted-foreground font-light">
                                            {slots[0] && format(new Date(slots[0].date), 'MMMM do, yyyy')} at {slots[0]?.time}
                                        </p>
                                        <p className="text-xs text-primary font-medium uppercase tracking-wider text-[10px]">
                                            {selectedThemes.map(t => t.name).join(' & ')}
                                        </p>
                                    </div>
                                </div>
                                <span className="text-lg font-serif text-foreground">{formatMYR(packagePrice)}</span>
                            </div>

                            {/* Addons */}
                            {selectedAddons.length > 0 && (
                                <div className="space-y-4 pt-8 border-t border-border/50">
                                    {selectedAddons.map(sa => {
                                        const addon = addonsList.find(a => a.id === sa.addonId);
                                        return (
                                            <div key={sa.addonId} className="flex justify-between items-center text-sm">
                                                <span className="text-muted-foreground font-light">
                                                    {addon?.name} <span className="ml-1 text-[10px] text-muted-foreground/40 font-bold tabular-nums">×{sa.quantity}</span>
                                                </span>
                                                <span className="font-medium text-foreground">
                                                    {formatMYR((addon?.price || 0) * sa.quantity)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Total */}
                            <div className="pt-8 border-t-2 border-border flex justify-between items-end">
                                <span className="text-sm font-medium text-muted-foreground uppercase tracking-widest mb-1.5">Total Amount</span>
                                <span className="text-4xl font-serif text-foreground">{formatMYR(totalPrice)}</span>
                            </div>

                            {themePrice > 0 && selectedThemes[0] && (
                                <div className="pt-6 border-t border-border/50 flex justify-between items-end">
                                    <div className="flex flex-col mb-1.5">
                                        <span className="text-sm font-medium text-foreground uppercase tracking-widest mb-1.5">Theme</span>
                                        <span className="text-[11px] text-muted-foreground font-light">{selectedThemes[0].name}</span>
                                    </div>
                                    <span className="text-2xl font-serif text-foreground">{formatMYR(themePrice)}</span>
                                </div>
                            )}

                            {/* Booking Deposit */}
                            <div className="pt-6 border-t border-border/50 flex justify-between items-end">
                                <div className="flex flex-col mb-1.5">
                                    <span className="text-sm font-medium text-foreground uppercase tracking-widest mb-1.5">Booking Deposit (Pay Now)</span>
                                    <span className="text-[11px] text-muted-foreground font-light">Required to secure your reservation</span>
                                </div>
                                <span className="text-4xl font-serif text-primary">{formatMYR(50)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-muted/30 rounded-[2rem] p-8 border border-border/50 flex items-center justify-between">
                        <div className="space-y-1">
                            <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground/60 mb-2">Guest Contact</p>
                            <p className="text-base font-serif text-foreground leading-none">{customerInfo.name}</p>
                            <p className="text-xs text-muted-foreground font-light">{customerInfo.email} • {customerInfo.phone}</p>
                            {customerInfo.notes && (
                                <p className="text-xs text-muted-foreground/80 font-light italic mt-1">&quot;{customerInfo.notes}&quot;</p>
                            )}
                        </div>
                        <Phone className="w-5 h-5 text-primary/40" />
                    </div>
                </motion.div>

                {/* Right: Payment */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="space-y-8"
                >
                    <div className="bg-muted/10 rounded-[2.5rem] p-10 border border-dashed border-border/80">
                        <h3 className="text-2xl font-serif text-foreground mb-6">Settlement</h3>

                        <div className="flex flex-col sm:flex-row items-center gap-8 bg-card p-6 rounded-3xl border border-border/50 shadow-sm mb-8">
                            <div className="relative group">
                                <div className="w-40 h-40 bg-white p-2 rounded-2xl border border-border/50 flex items-center justify-center overflow-hidden">
                                    <Image
                                        src={getImageUrl("/qr-bank.jpeg")}
                                        alt="DuitNow QR"
                                        width={160}
                                        height={160}
                                        className="object-contain"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-black/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className="gap-2"
                                        onClick={handleDownloadQr}
                                    >
                                        <Download className="w-4 h-4" />
                                        Save
                                    </Button>
                                </div>
                            </div>
                            <div className="flex-1 text-center sm:text-left space-y-4">
                                <div>
                                    <p className="text-sm font-medium text-foreground uppercase tracking-wider mb-1">DuitNow QR / Bank Transfer</p>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Maybank</p>
                                    <p className="text-sm font-serif text-foreground mt-0.5">Leqiu Creative</p>
                                </div>

                                <div className="flex items-center justify-center sm:justify-start gap-2">
                                    <code className="text-sm bg-muted/50 px-3 py-1.5 rounded-lg border border-border/50 font-mono text-foreground font-medium">5540 6232 8498</code>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="w-8 h-8 rounded-full hover:bg-card"
                                        onClick={handleCopy}
                                    >
                                        {isCopied ? (
                                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                                        ) : (
                                            <ClipboardIcon className="w-3.5 h-3.5 text-primary" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <a
                                href="https://wa.me/60132688407"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-4 rounded-2xl bg-muted/20 hover:bg-muted/40 transition-colors border border-border/50 group"
                            >
                                <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                                    <Phone className="w-4 h-4 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-foreground">Fauzi</p>
                                    <p className="text-[10px] text-muted-foreground">+60 13-268 8407</p>
                                </div>
                            </a>
                            <a
                                href="https://wa.me/60187882436"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-4 rounded-2xl bg-muted/20 hover:bg-muted/40 transition-colors border border-border/50 group"
                            >
                                <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                                    <Phone className="w-4 h-4 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-foreground">Izat</p>
                                    <p className="text-[10px] text-muted-foreground">+60 18-788 2436</p>
                                </div>
                            </a>
                        </div>

                        <div className="space-y-8">
                            <div className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.2em] font-medium text-foreground">Important Notes</h4>
                                <ul className="space-y-2 text-xs text-muted-foreground font-light list-disc pl-4">
                                    <li>Minimum RM50 deposit. Full payment can be made at the studio or online.</li>
                                    <li>The studio can reject / cancel the application if the payment is not made within one hour.</li>
                                    {module === "convocation" && (
                                        <li>Send the payment proof to the studio WhatsApp after submitting the booking.</li>
                                    )}
                                </ul>
                            </div>

                            <div className="flex gap-4 p-4 bg-primary/5 rounded-2xl border border-primary/10">
                                <AlertCircle className="w-5 h-5 text-primary shrink-0 opacity-60" />
                                <p className="text-xs text-primary/80 leading-relaxed italic">Kindly capture a screenshot of your successful transaction and provide it via WhatsApp for final verification.</p>
                            </div>

                            <div className="flex items-start space-x-4 pt-4">
                                <Checkbox
                                    id="confirm"
                                    checked={isConfirmed}
                                    onCheckedChange={handleConfirmChange}
                                    className="mt-1 w-5 h-5 rounded-lg border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                />
                                <Label htmlFor="confirm" className="text-xs leading-[1.6] text-muted-foreground font-light cursor-pointer select-none">
                                    I acknowledge that the reservation details are accurate and I commit to providing the settlement proof via our official WhatsApp channel.
                                </Label>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};
